import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { RefreshCw, Search } from 'lucide-react';
import API from '../../../util/API';
import ADMIN_PATHS from '../ADMIN_PATHS';

const HEX24_RE = /^[0-9a-fA-F]{24}$/;

const cx = (...arr) => arr.filter(Boolean).join(' ');

const Card = ({ children, className }) => (
  <div className={cx('bg-white border border-slate-200 rounded-2xl', className)}>{children}</div>
);

const SectionHead = ({ title, subtitle, right }) => (
  <div className="flex items-start justify-between gap-3">
    <div className="min-w-0">
      <div className="flex items-center gap-2">
        <span className="h-4 w-[3px] rounded-full bg-primary" aria-hidden="true" />
        <h2 className="text-base md:text-lg font-black tracking-tight text-slate-900">{title}</h2>
      </div>
      {subtitle ? <p className="mt-1 text-sm text-slate-500">{subtitle}</p> : null}
    </div>
    {right ? <div className="shrink-0">{right}</div> : null}
  </div>
);

const formatDateTime = (value) => {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString();
};

const ActionPill = ({ status }) => {
  const s = String(status || '').toLowerCase();
  const cls =
    s === 'success'
      ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
      : s === 'failed'
        ? 'border-rose-200 bg-rose-50 text-rose-700'
        : s === 'warning'
          ? 'border-amber-200 bg-amber-50 text-amber-700'
          : 'border-slate-200 bg-slate-50 text-slate-700';

  return (
    <span className={cx('inline-flex items-center rounded-full border px-2.5 py-1 text-[11px] font-black uppercase', cls)}>
      {status || 'info'}
    </span>
  );
};

// Order-area audit entities:
// - entityType 'Order' → order.cancel (order.controller.js).
// - entityType 'AliExpressOrder' → aliexpress.order.create (aePlaceOrder.service.js),
//   whose entityId is the public order id (out_order_id = orderId).
const ENTITY_TYPES = ['Order', 'AliExpressOrder'];

const getEntityLink = (log) => {
  const entityId = String(log?.entityId || '').trim();
  if (!entityId) return '';
  if (HEX24_RE.test(entityId)) return `${ADMIN_PATHS.ORDERS_PAGES.DETAILS}/${entityId}`;
  if (log?.entityType === 'AliExpressOrder') return `${ADMIN_PATHS.ORDERS_PAGES.DETAILS}/${entityId}`;
  return '';
};

const StatusLogs = () => {
  const [logs, setLogs] = useState([]);
  const [pageStatus, setPageStatus] = useState('idle'); // idle | loading | failed | succeeded
  const [loadError, setLoadError] = useState('');
  const [search, setSearch] = useState('');

  const loadLogs = useCallback(async () => {
    setPageStatus('loading');
    setLoadError('');
    try {
      const responses = await Promise.all(
        ENTITY_TYPES.map((entityType) =>
          API.get('/log', { params: { entityType, limit: 100 } }).catch(() => null)
        )
      );
      const merged = responses
        .filter(Boolean)
        .flatMap((res) => (Array.isArray(res?.data?.data) ? res.data.data : []));
      merged.sort((a, b) => new Date(b?.createdAt || 0) - new Date(a?.createdAt || 0));
      setLogs(merged);
      setPageStatus('succeeded');
    } catch (err) {
      setLogs([]);
      setPageStatus('failed');
      setLoadError(
        err?.response?.data?.message
        || err?.response?.data?.error
        || err?.message
        || 'Failed to load logs'
      );
    }
  }, []);

  useEffect(() => {
    loadLogs();
  }, [loadLogs]);

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    const list = Array.isArray(logs) ? logs : [];
    if (!q) return list;
    return list.filter((log) =>
      [log?.action, log?.entityType, log?.entityId, log?.message, log?.actorEmail, log?.actorUsername]
        .some((value) => String(value || '').toLowerCase().includes(q))
    );
  }, [logs, search]);

  return (
    <div className="p-6 space-y-6">
      <SectionHead
        title="Status Logs"
        subtitle="Admin audit-log entries for the order area, from GET /log (entity types Order and AliExpressOrder)."
        right={
          <button
            type="button"
            onClick={loadLogs}
            disabled={pageStatus === 'loading'}
            className={cx(
              'inline-flex items-center gap-2 rounded-2xl bg-slate-900 px-4 py-2 text-sm font-semibold text-white hover:bg-primary',
              pageStatus === 'loading' ? 'opacity-60 cursor-not-allowed' : ''
            )}
          >
            <RefreshCw className={cx('w-4 h-4', pageStatus === 'loading' ? 'animate-spin' : '')} />
            Refresh
          </button>
        }
      />

      <div className="rounded-2xl border border-slate-200 bg-slate-50 px-5 py-3 text-xs text-slate-600">
        There is no per-order status-history endpoint; this is the global admin audit log
        (GET /log) filtered to order-related entities, most recent 100 per entity type. Only
        significant events are recorded (order cancellation, AliExpress forwarding) — payments
        finalize/approve transitions are not logged as history rows yet.
      </div>

      {pageStatus === 'failed' ? (
        <div className="rounded-2xl border border-rose-200 bg-rose-50 px-5 py-4 text-sm text-rose-800">
          {loadError}
        </div>
      ) : null}

      <Card className="p-5">
        <div className="relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            type="text"
            placeholder="Filter by action, order id, message, actor…"
            className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-slate-200 focus:ring-2 focus:ring-primary/25 focus:border-primary text-sm"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
      </Card>

      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full">
            <thead className="bg-slate-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-black tracking-wide text-slate-600 uppercase">Time</th>
                <th className="px-4 py-3 text-left text-xs font-black tracking-wide text-slate-600 uppercase">Action</th>
                <th className="px-4 py-3 text-left text-xs font-black tracking-wide text-slate-600 uppercase">Order</th>
                <th className="px-4 py-3 text-left text-xs font-black tracking-wide text-slate-600 uppercase">Message</th>
                <th className="px-4 py-3 text-left text-xs font-black tracking-wide text-slate-600 uppercase">Actor</th>
                <th className="px-4 py-3 text-left text-xs font-black tracking-wide text-slate-600 uppercase">Result</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {pageStatus === 'loading' ? (
                <tr>
                  <td colSpan={6} className="px-4 py-8 text-center text-sm text-slate-500">
                    Loading order logs…
                  </td>
                </tr>
              ) : rows.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-4 py-8 text-center text-sm text-slate-500">
                    {pageStatus === 'failed'
                      ? 'Could not load logs.'
                      : search
                        ? 'No logs match the filter.'
                        : 'No order-related audit logs yet.'}
                  </td>
                </tr>
              ) : (
                rows.map((log) => {
                  const link = getEntityLink(log);
                  return (
                    <tr key={log._id} className="hover:bg-slate-50 transition-colors align-top">
                      <td className="px-4 py-3 text-sm text-slate-600 whitespace-nowrap">
                        {formatDateTime(log?.createdAt)}
                      </td>
                      <td className="px-4 py-3">
                        <span className="font-mono text-xs font-bold text-slate-900 break-all">
                          {String(log?.action || '—')}
                        </span>
                        <p className="mt-0.5 text-[11px] text-slate-500">{String(log?.entityType || '')}</p>
                      </td>
                      <td className="px-4 py-3 font-mono text-xs text-slate-700 break-all">
                        {link ? (
                          <Link to={link} className="font-semibold text-primary hover:underline">
                            {String(log?.entityId || '—')}
                          </Link>
                        ) : (
                          String(log?.entityId || '—')
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-700 max-w-[360px]">
                        <p className="line-clamp-2">{String(log?.message || '—')}</p>
                        {log?.meta?.reason ? (
                          <p className="mt-0.5 text-xs text-slate-500">
                            reason: <span className="font-semibold text-slate-700">{String(log.meta.reason)}</span>
                          </p>
                        ) : null}
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-700">
                        <p className="font-semibold">{log?.actorUsername || log?.actorEmail || log?.actorType || '—'}</p>
                        {log?.actorEmail && log?.actorUsername ? (
                          <p className="text-xs text-slate-500 break-all">{log.actorEmail}</p>
                        ) : null}
                      </td>
                      <td className="px-4 py-3">
                        <ActionPill status={log?.status} />
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
};

export default StatusLogs;
